import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import EmailInput from '../components/EmailInput';
import PasswordInput from '../components/PasswordInput';
import { useAuth } from '../hooks/useAuth';
import { SwatchBook } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated, silentSignOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from || '/projects';
  const message = location.state?.message || null;

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    const form = e.currentTarget;

    // *** FINAL FIX: Use querySelector for maximum reliability. ***
    // This finds the inputs by type, which is independent of the custom component's implementation
    // and is not blocked by browser privacy features.
    const emailInput = form.querySelector('input[type="email"]');
    const passwordInput = form.querySelector('input[type="password"]');

    // Use state for typed input, but fall back to the DOM value for autofill.
    const currentEmail = email || emailInput?.value;
    const currentPassword = password || passwordInput?.value;

    if (!currentEmail || !currentPassword) {
      setError('Username and password are required.');
      setIsLoading(false);
      return;
    }

    let retried = false;
    try {
      await login(currentEmail, currentPassword);
      // Redirection happens in useEffect
    } catch (err) {
      if ((err.code === 'AlreadySignedInException' || err.message?.includes('already a signed in user')) && !retried) {
        try {
          await silentSignOut();
        } catch (logoutErr) {
          // Ignore silent sign out errors
        }
        retried = true;
        try {
          await login(currentEmail, currentPassword);
        } catch (err2) {
          if (err2.code === 'UserNotConfirmedException') {
            navigate('/verify-email', { state: { email: currentEmail } });
          } else {
            setError(err2.message || 'Failed to login. Please check your credentials.');
          }
        }
      } else if (err.code === 'UserNotConfirmedException') {
        navigate('/verify-email', { state: { email: currentEmail } });
      } else {
        setError(err.message || 'Failed to login. Please check your credentials.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-md mx-auto px-4">
        <Link 
          to="/" 
          className="flex flex-col items-center justify-center mb-6 transform transition-transform duration-300 hover:scale-105"
        >
          <SwatchBook className="w-12 h-12 text-orange-500 mb-2" />
          <span className="text-2xl font-bold text-black">dessino.ai</span>
        </Link>
        
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h1 className="text-2xl font-bold mb-5">Login</h1>
          
          {message && (
            <div className="mb-4 p-3 bg-blue-100 text-blue-700 rounded-md">
              {message}
            </div>
          )}
          
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <EmailInput
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
              />
            </div>
            <div className="mb-4">
              <PasswordInput
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-black text-white py-3 rounded-md hover:bg-gray-800 transition-all duration-300 flex items-center justify-center transform hover:scale-[1.01] active:scale-[0.99]"
            >
              {isLoading ? (
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2"></span>
              ) : null}
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>
          
          <p className="text-center text-sm text-gray-600 mt-4">
            Forgot your password?{' '}
            <Link to="/password-reset" className="text-blue-500 hover:underline">Reset Password</Link>
          </p>
          
          <p className="text-center text-sm text-gray-600 mt-2">
            Don't have an account?{' '}
            <Link to="/signup" className="text-blue-500 hover:underline">Sign Up</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;